import { useEffect, useState } from "react";
import { Trophy, Timer, Bot } from "lucide-react";
import { api } from "@/lib/api";

const MEDALS = ["🥇", "🥈", "🥉"];

export const ReferralContestBanner = ({ accountId }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const q = accountId ? `?account_id=${encodeURIComponent(accountId)}` : "";
        const res = await api.get(`/referral/contest${q}`);
        if (!cancelled) setData(res.data);
      } catch { /* banner is optional */ }
    })();
    return () => { cancelled = true; };
  }, [accountId]);

  if (!data?.enabled) return null;
  const prizes = (data.prizes || []).filter(Boolean);
  const endsPretty = data.ends_at
    ? new Date(data.ends_at + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : "";

  return (
    <div data-testid="referral-contest-banner"
      className="relative overflow-hidden rounded-xl border border-amber-400/50 bg-gradient-to-br from-amber-500/15 via-vts-surface to-vts-blue/10 p-4 space-y-3">
      <div className="absolute -top-10 -left-10 h-32 w-32 rounded-full bg-amber-400/15 blur-2xl pointer-events-none" />
      <div className="flex items-center gap-2 flex-wrap">
        <Trophy className="h-5 w-5 text-amber-400" />
        <p className="text-sm font-bold text-white uppercase tracking-wide">Referral Contest</p>
        {endsPretty && <span className="text-[10px] font-mono text-vts-silver/60">· ends {endsPretty}</span>}
        <span data-testid="contest-days-left"
          className="ml-auto flex items-center gap-1 text-[11px] font-mono font-bold text-amber-300 bg-amber-500/10 border border-amber-500/40 rounded-full px-2.5 py-0.5">
          <Timer className="h-3 w-3" /> {data.days_left}d left
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {prizes.map((p, i) => (
          <span key={i} data-testid={`contest-prize-${i + 1}`}
            className={`text-[11px] font-mono font-bold rounded-lg px-2.5 py-1 border ${
              i === 0 ? "bg-amber-500/20 text-amber-300 border-amber-500/50"
                : "bg-vts-surface/60 text-vts-silver/80 border-vts-line"}`}>
            {MEDALS[i]} {p}
          </span>
        ))}
        <span className="text-[10px] text-vts-silver/55 self-center">most bot-active referrals wins</span>
      </div>

      <div data-testid="contest-rule" className="flex items-start gap-1.5 rounded-lg border border-vts-blue/30 bg-vts-blue/10 px-2.5 py-1.5">
        <Bot className="h-3.5 w-3.5 text-vts-blue shrink-0 mt-0.5" />
        <p className="text-[10px] text-vts-silver/80 leading-snug">
          A referral only counts once they <b className="text-white">run a bot</b> (Auto Bot, Quick Digit Bot or Copy Trading).
          Prizes are <b className="text-amber-300">ON TOP</b> of your 10–20% lifetime commission on everything they trade.
        </p>
      </div>

      {(data.standings || []).length > 0 && (
        <div data-testid="contest-standings" className="rounded-lg border border-vts-line bg-black/30 p-2.5 space-y-1">
          {data.standings.slice(0, 3).map((s, i) => (
            <div key={s.rank} className="flex items-center gap-2 text-xs font-mono">
              <span>{MEDALS[i]}</span>
              <span className={s.named ? "font-bold text-white" : "text-vts-silver/80"}>{s.account}</span>
              <span className="ml-auto text-amber-300">{s.referrals} bot-active</span>
            </div>
          ))}
        </div>
      )}

      {data.me && (
        <p data-testid="contest-my-rank" className="text-[11px] font-mono text-vts-silver/70">
          {data.me.rank
            ? data.me.rank === 1
              ? <>👑 <b className="text-amber-300">You're in the lead</b> with {data.me.referrals} bot-active referral{data.me.referrals === 1 ? "" : "s"} — keep sharing to hold it!</>
              : <>You're <b className="text-white">#{data.me.rank}</b> with {data.me.referrals} — <b className="text-amber-300">{data.me.to_first} more</b> to take 🥇</>
            : <>No qualified entries yet — <b className="text-amber-300">{data.me.to_first} bot-active referral{data.me.to_first === 1 ? "" : "s"}</b> takes the lead. Share your link below 👇</>}
          {data.me.pending > 0 && (
            <span data-testid="contest-pending" className="block mt-0.5 text-vts-blue">
              ⏳ {data.me.pending} of your referrals signed up but haven't run a bot yet — nudge them to switch one on!
            </span>
          )}
        </p>
      )}
      {data.note && <p className="text-[9px] text-vts-silver/45 font-mono">{data.note}</p>}
    </div>
  );
};

export default ReferralContestBanner;
